import React, { useState, useEffect } from 'react'
import { useForm, useError } from '../../hooks'
import TextField from '../Form/TextField'
import DropdownMenu, { DropdownItem } from '../DropdownMenu'
import Button from '../Button'
import Spinner from '../Spinner'
import { getFirestore, doc, getDoc, updateDoc, deleteField } from 'firebase/firestore'
import { db } from '../../firebase'

// ✅ --- PersonalDataForm Component ---
function PersonalDataForm({ user, id, type, value, isNew, onChange }) {
  const setError = useError()

  // ✅ Initialize form with current value
  const [getFieldProps, formData, formError, setFormData] = useForm()
  useEffect(() => {
    setFormData({ value }) // Pre-fill form with existing value
  }, [value, setFormData])

  const edit = formData?.value !== value

  async function deleteData() {
    try {
      if (window.confirm('Are you sure you wish to delete this item?')) {
        const userRef = doc(db, 'users', user.id)

        await updateDoc(userRef, {
          [type.typeName]: deleteField()
        })

        onChange()
      }
    } catch (err) {
      setError(err)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const updatedValue = formData?.value ?? ''

    try {
      const userRef = doc(db, 'users', user.id)

      await updateDoc(userRef, {
        [type.typeName]: updatedValue
      })

      onChange()
    } catch (err) {
      if (err.message === 'validation_error') {
        formError(err.props)
      } else {
        setError(err)
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginBottom: '1em' }}>
      <TextField
        name="value"
        defaultValue={value}
        label={type.displayName}
        placeholder={type.displayName}
        renderAddons={() =>
          edit ? (
            <button type="submit" className="button is-primary">
              Save
            </button>
          ) : (
            <button
              type="button"
              onClick={isNew ? onChange : deleteData}
              className={`button ${isNew ? 'is-warning' : 'is-danger'}`}
            >
              Delete
            </button>
          )
        }
        {...getFieldProps()}
      />
    </form>
  )
}

// ✅ --- Main Component ---
export default function ({ id, onFinishEditing }) {
  const [user, setUser] = useState(null)
  const [personalDataTypes, setPersonalDataTypes] = useState([])
  const [newItems, setNewItems] = useState([])

  async function fetchUser() {
    const userSnap = await getDoc(doc(db, 'users', id))
    if (userSnap.exists()) {
      setUser({ id: userSnap.id, ...userSnap.data() })
    }
  }

  async function fetchPersonalDataTypes() {
    setPersonalDataTypes([
      { typeName: 'displayName', displayName: 'Display Name', allowMany: false },
      { typeName: 'phone', displayName: 'Phone Number', allowMany: false }
    ])
  }

  useEffect(() => {
    fetchUser()
    fetchPersonalDataTypes()
  }, [])

  function addItem(type) {
    setNewItems([...newItems, { type, value: '', id: 10000 + newItems.length }])
  }

  if (!user || !personalDataTypes.length) {
    return <Spinner />
  }

  const existingTypes = personalDataTypes.reduce((acc, type) => {
    if (user[type.typeName]) acc[type.typeName] = true
    return acc
  }, {})

  const availableTypes = personalDataTypes.filter(
    (type) => !existingTypes[type.typeName] || type.allowMany
  )

  return (
    <div>
      <div>
        {personalDataTypes
          .filter((type) => user[type.typeName])
          .map((type) => ({
            id: type.typeName,
            type,
            value: user[type.typeName]
          }))
          .map((data) => (
            <PersonalDataForm
              key={data.id}
              user={user}
              onChange={fetchUser}
              {...data}
            />
          ))}

        {newItems.map((data, index) => (
          <PersonalDataForm
            key={data.id}
            user={user}
            onChange={() => {
              let items = [...newItems]
              items.splice(index, 1)
              setNewItems(items)
              fetchUser()
            }}
            isNew={true}
            {...data}
          />
        ))}
      </div>
      <br />
      <div className="level">
        <div className="level-left">
          <div className="level-item">
            <DropdownMenu label="Add new item">
              {availableTypes.map((type) => (
                <DropdownItem key={type.typeName} onClick={() => addItem(type)}>
                  {type.displayName}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </div>
          <div className="level-item">
            <Button onClick={onFinishEditing}>Finish editing</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
